class CgFeedingWizard extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.form = this.querySelector('[data-wizard-form]');
    this.steps = [...this.querySelectorAll('[data-wizard-step]')];
    this.progressItems = [...this.querySelectorAll('[data-wizard-progress-item]')];
    this.result = this.querySelector('[data-wizard-result]');
    this.navigation = this.querySelector('[data-wizard-navigation]');
    this.backButton = this.querySelector('[data-wizard-back]');
    this.nextButton = this.querySelector('[data-wizard-next]');
    this.calculateButton = this.querySelector('[data-wizard-calculate]');
    this.currentStep = 0;

    if (!this.form || !this.steps.length || !this.result) return;

    this.form.addEventListener('input', () => {
      this.markResultStale();
      this.syncControls();
    }, { signal });
    this.form.addEventListener('change', () => {
      this.markResultStale();
      this.syncControls();
    }, { signal });
    this.querySelectorAll('[data-wizard-choice]').forEach((button) => {
      button.addEventListener('click', () => this.selectChoice(button), { signal });
    });
    this.nextButton?.addEventListener('click', () => this.goNext(), { signal });
    this.backButton?.addEventListener('click', () => this.goBack(), { signal });
    this.calculateButton?.addEventListener('click', () => this.calculate(), { signal });
    this.querySelector('[data-wizard-edit]')?.addEventListener('click', () => this.edit(), { signal });

    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get activeStep() {
    return this.steps[this.currentStep];
  }

  isStepValid(step = this.activeStep) {
    if (!step) return false;
    const fieldsValid = [...step.querySelectorAll('input, select, textarea')].every((field) => field.checkValidity());
    const groupsValid = [...step.querySelectorAll('[data-wizard-choice-group]')]
      .every((group) => group.querySelector('[data-wizard-choice][aria-pressed="true"]'));
    return fieldsValid && groupsValid;
  }

  selectChoice(button) {
    const group = button.closest('[data-wizard-choice-group]');
    if (!group) return;
    group.querySelectorAll('[data-wizard-choice]').forEach((item) => {
      item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
    });
    this.markResultStale();
    this.syncControls();
  }

  showError(show) {
    const error = this.activeStep?.querySelector('[data-wizard-error]');
    if (error) error.hidden = !show;
  }

  goNext() {
    if (!this.isStepValid()) {
      this.showError(true);
      this.focusFirstControl();
      return;
    }
    this.showError(false);
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep += 1;
      this.render(true);
    }
  }

  goBack() {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
      this.render(true);
    }
  }

  calculate() {
    if (!this.isStepValid()) {
      this.showError(true);
      this.focusFirstControl();
      return;
    }
    this.steps.forEach((step) => { step.hidden = true; });
    this.result.hidden = false;
    this.navigation.hidden = true;
    this.syncProgress(this.steps.length);
    this.dispatchEvent(new CustomEvent('cg:wizard:calculate', {
      bubbles: true,
      detail: {
        calculatorType: this.dataset.calculatorType,
        values: this.collectValues(),
      },
    }));
    this.result.focus?.({ preventScroll: true });
  }

  edit() {
    this.result.hidden = true;
    this.navigation.hidden = false;
    this.currentStep = 0;
    this.render(true);
  }

  showResult({ dose, energy, duration, cost, note } = {}) {
    this.setMetric('dose', dose);
    this.setMetric('energy', energy);
    this.setMetric('duration', duration);
    this.setMetric('cost', cost);
    const noteElement = this.querySelector('[data-wizard-engine-note]');
    if (noteElement) {
      noteElement.textContent = note || '';
      noteElement.hidden = !note;
    }
  }

  collectValues() {
    const values = Object.fromEntries(new FormData(this.form));
    this.querySelectorAll('[data-wizard-choice][aria-pressed="true"]').forEach((choice) => {
      values[choice.dataset.wizardName] = choice.dataset.wizardValue;
    });
    return values;
  }

  markResultStale() {
    this.dataset.resultStale = 'true';
  }

  focusFirstControl() {
    this.activeStep?.querySelector('[data-wizard-choice], input, select, textarea')?.focus({ preventScroll: true });
  }

  setMetric(name, value) {
    const card = this.querySelector(`[data-wizard-${name}]`);
    const target = this.querySelector(`[data-wizard-${name}-value]`);
    const hasValue = value != null && value !== '';
    if (card) card.hidden = !hasValue;
    if (target && hasValue) target.textContent = String(value);
  }

  syncControls() {
    const valid = this.isStepValid();
    const isLast = this.currentStep === this.steps.length - 1;
    if (this.nextButton) {
      this.nextButton.hidden = isLast;
      this.nextButton.disabled = !valid;
      this.nextButton.setAttribute('aria-disabled', String(!valid));
    }
    if (this.calculateButton) {
      this.calculateButton.hidden = !isLast;
      this.calculateButton.disabled = !valid;
      this.calculateButton.setAttribute('aria-disabled', String(!valid));
    }
    if (valid) this.showError(false);
  }

  syncProgress(activeIndex = this.currentStep) {
    this.progressItems.forEach((item, index) => {
      if (index === activeIndex) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
      item.dataset.complete = index < activeIndex ? 'true' : 'false';
    });
  }

  render(focus = false) {
    this.steps.forEach((step, index) => { step.hidden = index !== this.currentStep; });
    this.result.hidden = true;
    this.navigation.hidden = false;
    if (this.backButton) this.backButton.hidden = this.currentStep === 0;
    this.syncProgress();
    this.syncControls();
    if (focus) this.focusFirstControl();
  }
}

if (!customElements.get('cg-feeding-wizard')) {
  customElements.define('cg-feeding-wizard', CgFeedingWizard);
}
